/**
 * Inicio: asignaciones del encuestador y estado de la cola.
 *
 * Es lo primero que ve al abrir la app en campo, así que concentra lo que
 * necesita saber de un vistazo: qué conglomerado le toca, cuánto lleva, si hay
 * algo sin enviar y si la cartografía está en el dispositivo.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { unfinishedDrafts, useApp } from '../store';
import { syncNow } from '../db/queue';
import type { DraftInterview } from '../db/db';
import { DataUsageCard } from '../components/DataUsageCard';
import { UpdatePrompt } from '../components/UpdatePrompt';

export function HomeScreen() {
  const {
    operator,
    assignments,
    pending,
    isOnline,
    packageFetchedAt,
    prefetchReport,
    loadFieldData,
    refreshPending,
    markSynced,
  } = useApp();
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState<DraftInterview[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    void refreshPending();
    void unfinishedDrafts().then(setDrafts);
  }, [refreshPending]);

  const doSync = async () => {
    setSyncing(true);
    try {
      await syncNow();
      markSynced();
      await refreshPending();
    } finally {
      setSyncing(false);
    }
  };

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      await loadFieldData({ prefetchGeometry: true });
    } finally {
      setRefreshing(false);
    }
  };

  const totalTarget = assignments.reduce((a, x) => a + x.targetInterviews, 0);
  const totalDone = assignments.reduce((a, x) => a + x.capturedInterviews, 0);

  return (
    <div className="app">
      {/* La marca sólo aparece en la pantalla de inicio. Dentro de la
          entrevista el espacio superior es para el avance y el bloque: meter
          un logotipo ahí le quitaría sitio a lo que el encuestador necesita
          mirar cada veinte segundos. */}
      <div className="topbar topbar--home">
        <div className="logo logo--sm" role="img" aria-label="Calimetría" />
        {/**
         * El bloque de identidad lleva a Ajustes.
         *
         * Antes la única puerta para cerrar sesión era un botón discreto al
         * final del inicio, debajo de las tarjetas y las asignaciones: con
         * cuatro conglomerados en pantalla había que desplazarse para
         * encontrarlo. Tocar el propio nombre es donde se busca la cuenta.
         */}
        <Link
          to="/ajustes"
          className="topbar__who"
          style={{ textDecoration: 'none', color: 'inherit' }}
          aria-label="Tu cuenta y cerrar sesión"
        >
          <div className="topbar__title">{operator?.name}</div>
          <div className="topbar__sub">{operator?.code} · Cuenta ›</div>
        </Link>
        <ConnectionBadge isOnline={isOnline} />
      </div>

      <main className="app__main">
        {/* Sólo en el inicio: es el momento «entre viviendas» en el que
            recargar no le cuesta nada al encuestador. */}
        <UpdatePrompt />

        {/* Nada sin enviar pasa desapercibido: es lo que podría perderse. */}
        {pending.interviews + pending.households > 0 ? (
          <div className="notice notice--warning">
            <span className="notice__title">
              {pending.interviews} entrevista(s) y {pending.households} vivienda(s) sin enviar
            </span>
            {isOnline
              ? 'Hay conexión. Envía ahora para que no se queden en el teléfono.'
              : 'Se enviarán automáticamente al recuperar la señal. No cierres sesión hasta entonces.'}
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void doSync()}
                disabled={!isOnline || syncing}
              >
                {syncing ? 'Enviando…' : 'Enviar ahora'}
              </button>
            </div>
          </div>
        ) : null}

        {pending.rejected > 0 ? (
          <div className="notice notice--danger">
            <span className="notice__title">{pending.rejected} registro(s) rechazados</span>
            El servidor no los aceptó. Revísalos con tu supervisor.
            <div style={{ marginTop: 10 }}>
              <Link to="/sincronizacion" className="btn btn--secondary">
                Ver detalle
              </Link>
            </div>
          </div>
        ) : null}

        {/* Un borrador abierto es una entrevista a medias que hay que cerrar. */}
        {drafts.length > 0 ? (
          <div className="notice notice--info">
            <span className="notice__title">
              {drafts.length} entrevista(s) sin terminar
            </span>
            {drafts.map((draft) => (
              <button
                key={draft.clientUuid}
                type="button"
                className="btn btn--secondary btn--block"
                style={{ marginTop: 8 }}
                onClick={() => navigate(`/entrevista/${draft.clientUuid}`)}
              >
                Retomar · sección {draft.expectedFrameSeccion ?? '?'} ·{' '}
                {new Date(draft.startedAt).toLocaleTimeString('es-MX', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </button>
            ))}
          </div>
        ) : null}

        <div className="row row--between" style={{ marginBottom: 12 }}>
          <h1 className="screen-title" style={{ margin: 0 }}>
            Mis conglomerados
          </h1>
          <span className="badge badge--neutral">
            {totalDone} / {totalTarget}
          </span>
        </div>

        {assignments.length === 0 ? (
          <div className="card">
            <p className="card__title">Sin asignaciones</p>
            <p className="card__meta">
              Tu coordinador aún no te ha asignado conglomerados, o no se han descargado.
            </p>
            <button
              type="button"
              className="btn btn--secondary"
              style={{ marginTop: 12 }}
              onClick={() => void doRefresh()}
              disabled={!isOnline || refreshing}
            >
              {refreshing ? 'Actualizando…' : 'Actualizar'}
            </button>
          </div>
        ) : (
          <ul className="list">
            {assignments.map((assignment) => {
              const done = assignment.capturedInterviews;
              const target = assignment.targetInterviews;
              const complete = done >= target;
              return (
                <li key={assignment.id}>
                  <Link
                    to={`/asignacion/${assignment.id}`}
                    className="card card--tappable"
                    style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className="row row--between">
                      <p className="card__title">
                        {assignment.cluster.id} · Sección {assignment.cluster.seccion}
                      </p>
                      <span className={`badge ${complete ? 'badge--ok' : 'badge--neutral'}`}>
                        {done} / {target}
                      </span>
                    </div>
                    <p className="card__meta">
                      {assignment.cluster.colonia}
                      {!assignment.cluster.coloniaConfirmed ? ' (referencia preliminar)' : ''}
                    </p>
                    <p className="card__meta">
                      Estrato {assignment.cluster.stratum.id} ·{' '}
                      {assignment.cluster.areaLabel}
                      {assignment.systematicSkipK !== null
                        ? ` · salto 1 de cada ${assignment.systematicSkipK}`
                        : ''}
                    </p>
                    <div className="progress" style={{ marginTop: 10 }}>
                      <div
                        className="progress__bar"
                        style={{ width: `${Math.min(100, (done / target) * 100)}%` }}
                      />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}

        <div className="divider" />

        <CartographyStatus prefetchReport={prefetchReport} assignmentCount={assignments.length} />

        <DataUsageCard isOnline={isOnline} />

        <div className="row row--wrap" style={{ marginTop: 16 }}>
          <Link to="/sincronizacion" className="btn btn--secondary">
            Sincronización
          </Link>
          <Link to="/ajustes" className="btn btn--secondary">
            Ajustes y cerrar sesión
          </Link>
        </div>

        {packageFetchedAt ? (
          <p className="faint" style={{ marginTop: 16 }}>
            Cuestionario descargado el{' '}
            {new Date(packageFetchedAt).toLocaleString('es-MX', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
            {isStale(packageFetchedAt) ? ' · conviene actualizarlo' : ''}
          </p>
        ) : null}
      </main>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

export function ConnectionBadge({ isOnline }: { isOnline: boolean }) {
  return (
    <span className={`badge ${isOnline ? 'badge--ok' : 'badge--warn'}`}>
      <span className={`dot ${isOnline ? 'dot--ok' : 'dot--warn'}`} />
      {isOnline ? 'En línea' : 'Sin señal'}
    </span>
  );
}

/**
 * Estado de la cartografía en el dispositivo.
 *
 * Sin los polígonos cacheados la PWA no puede validar el GPS sin señal, y §9.4
 * exige GPS en cada captura. Mejor decirlo en la pantalla de inicio que
 * descubrirlo en la puerta de la primera vivienda.
 */
function CartographyStatus({
  prefetchReport,
  assignmentCount,
}: {
  prefetchReport: { sections: number[]; clusters: string[]; failed: { target: string; reason: string }[] } | null;
  assignmentCount: number;
}) {
  if (assignmentCount === 0) return null;

  if (!prefetchReport) {
    return (
      <div className="notice notice--info">
        <span className="notice__title">Cartografía no verificada</span>
        Conéctate a WiFi y usa «Actualizar» para descargar los polígonos de tus secciones. Sin
        ellos no se puede validar la ubicación sin señal.
      </div>
    );
  }

  if (prefetchReport.failed.length > 0) {
    return (
      <div className="notice notice--warning">
        <span className="notice__title">Cartografía incompleta</span>
        Se descargaron {prefetchReport.sections.length} sección(es) y{' '}
        {prefetchReport.clusters.length} conglomerado(s), pero faltaron:{' '}
        {prefetchReport.failed.map((f) => f.target).join(', ')}. Reintenta con WiFi.
      </div>
    );
  }

  return (
    <div className="notice notice--success">
      <span className="notice__title">Cartografía lista para trabajar sin señal</span>
      {prefetchReport.sections.length} sección(es) y {prefetchReport.clusters.length}{' '}
      conglomerado(s) en el dispositivo.
    </div>
  );
}

/** Más de 24 h sin actualizar: pudo cambiar un recorte o una asignación. */
function isStale(fetchedAt: number): boolean {
  return Date.now() - fetchedAt > 1000 * 60 * 60 * 24;
}
